// Toast context. The provider (components/ToastZone.tsx) owns the queue
// and renders the one persistent live region; consumers only call
// show()/dismiss(). Kept in lib/ so routes and components import the
// hook without pulling the zone's JSX.

import { createContext, useContext } from "react";

export type ToastTone = "ok" | "err";

/** Duración de los toasts del módulo Administración (handoff
 *  autogestión: 2,6 s). Se pasa como `{ durationMs: ADMIN_TOAST_MS }`
 *  desde las pantallas nuevas; el default global (3,2 s) no cambia para
 *  no acortar los avisos de las pantallas que ya existen. */
export const ADMIN_TOAST_MS = 2600;

/** Opciones de show() para las pantallas de Administración: duración
 *  del handoff y check en el tono ok. Opt-in para que los toasts de
 *  postventa y arriendos se vean igual que antes. */
export const ADMIN_TOAST_OPTS = { durationMs: ADMIN_TOAST_MS, icon: true } as const;

export type ToastOptions = {
  durationMs?: number;
  /** Check decorativo antes del mensaje (solo tono ok). */
  icon?: boolean;
};

export type ToastItem = {
  id: number;
  message: string;
  tone: ToastTone;
  /** Fade class applied at durationMs; the node stays in the DOM ~1.3s
   *  longer so assistive tech finishes reading it. */
  leaving: boolean;
  icon?: boolean;
};

export type ToastApi = {
  show: (message: string, tone?: ToastTone, opts?: ToastOptions) => void;
  /** Without an id, clears every toast. */
  dismiss: (id?: number) => void;
};

let warned = false;

// No-op fallback rather than a throw: a route rendered outside the Shell
// (or a test that forgot the provider) must not crash on a toast — the
// message is a courtesy, never the source of truth. Warned once in dev.
export const ToastContext = createContext<ToastApi>({
  show: () => {
    if (import.meta.env.DEV && !warned) {
      warned = true;
      console.warn("useToast(): no ToastProvider above this tree — toast dropped.");
    }
  },
  dismiss: () => {},
});

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
